import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';

export interface MenuItem {
    id: string;
    label: string;
    icon?: React.ReactNode;
    onSelect: () => void;
    danger?: boolean;
    disabled?: boolean;
    /** Shown as the item's title when disabled, so the user learns why. */
    disabledReason?: string;
}

export type MenuEntry = MenuItem | 'separator';

interface LibraryMenuProps {
    label: string;
    items: MenuEntry[];
    /** Trigger content; defaults to a kebab icon. */
    children?: React.ReactNode;
    triggerClassName?: string;
    align?: 'left' | 'right';
    /** Preferred opening direction; flips automatically when there is no room. */
    direction?: 'down' | 'up';
    onOpenChange?: (open: boolean) => void;
    /** -1 keeps the trigger out of the Tab order (tree rows open it with Shift+F10 instead). */
    triggerTabIndex?: number;
    /** The layer the panel opens on: above the default on a page that itself covers the canvas. */
    layer?: number;
}

const GAP = 4;
const MENU_LAYER = 60;

/**
 * Accessible dropdown menu (kebab). The panel renders in a portal with fixed
 * positioning so cards with overflow:hidden never clip it. Clicks inside never
 * bubble to the parent card, so a card's own onClick (open) does not fire.
 */
export const LibraryMenu: React.FC<LibraryMenuProps> = ({
    label,
    items,
    children,
    triggerClassName = 'ns-icon-btn',
    align = 'right',
    direction = 'down',
    onOpenChange,
    triggerTabIndex,
    layer = MENU_LAYER,
}) => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const menuId = useId();

    const setOpenState = useCallback((next: boolean) => {
        setOpen(next);
        onOpenChange?.(next);
    }, [onOpenChange]);

    const place = useCallback(() => {
        const trigger = triggerRef.current;
        const menu = menuRef.current;
        if (!trigger || !menu) return;
        const t = trigger.getBoundingClientRect();
        const m = menu.getBoundingClientRect();
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        let top = direction === 'up' ? t.top - m.height - GAP : t.bottom + GAP;
        if (direction === 'down' && top + m.height > vh - 8 && t.top - m.height - GAP > 8) top = t.top - m.height - GAP;
        if (direction === 'up' && top < 8 && t.bottom + GAP + m.height < vh - 8) top = t.bottom + GAP;
        top = Math.max(8, Math.min(top, vh - m.height - 8));
        let left = align === 'right' ? t.right - m.width : t.left;
        left = Math.max(8, Math.min(left, vw - m.width - 8));
        setPos({ top, left });
    }, [align, direction]);

    useLayoutEffect(() => {
        if (!open) { setPos(null); return; }
        place();
    }, [open, place]);

    useEffect(() => {
        if (!open) return;
        const onPointerDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
            setOpenState(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.stopPropagation();
                setOpenState(false);
                triggerRef.current?.focus();
            }
        };
        const onScrollOrResize = () => place();
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKey, true);
        window.addEventListener('resize', onScrollOrResize);
        window.addEventListener('scroll', onScrollOrResize, true);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKey, true);
            window.removeEventListener('resize', onScrollOrResize);
            window.removeEventListener('scroll', onScrollOrResize, true);
        };
    }, [open, setOpenState, place]);

    // Focus the first item only once the panel is positioned (it is visibility:hidden before).
    useEffect(() => {
        if (!open || !pos) return;
        menuRef.current?.querySelector<HTMLButtonElement>('button[role="menuitem"]:not(:disabled)')?.focus();
    }, [open, pos]);

    const onMenuKeyDown = (e: React.KeyboardEvent) => {
        const buttons = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('button[role="menuitem"]:not(:disabled)') || []);
        if (buttons.length === 0) return;
        const index = buttons.indexOf(document.activeElement as HTMLButtonElement);
        if (e.key === 'ArrowDown') { e.preventDefault(); buttons[(index + 1) % buttons.length].focus(); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); buttons[(index - 1 + buttons.length) % buttons.length].focus(); }
        else if (e.key === 'Home') { e.preventDefault(); buttons[0].focus(); }
        else if (e.key === 'End') { e.preventDefault(); buttons[buttons.length - 1].focus(); }
        else if (e.key === 'Tab') { setOpenState(false); triggerRef.current?.focus(); }
    };

    const stop = (e: React.SyntheticEvent) => e.stopPropagation();

    const panel = open ? (
        <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-label={label}
            data-ns-layer="menu"
            className="ns-menu"
            // Above every bar, panel and the phone drawer (50), under a dialog's scrim (100)
            style={{ position: 'fixed', top: pos?.top ?? -9999, left: pos?.left ?? -9999, zIndex: layer, visibility: pos ? 'visible' : 'hidden' }}
            onKeyDown={(e) => { stop(e); onMenuKeyDown(e); }}
            onClick={stop}
            onMouseDown={stop}
        >
            {items.map((item, i) =>
                item === 'separator' ? (
                    <div key={`sep-${i}`} className="ns-menu-sep" role="separator" />
                ) : (
                    <button
                        key={item.id}
                        type="button"
                        role="menuitem"
                        className="ns-menu-item"
                        data-danger={item.danger ? 'true' : undefined}
                        disabled={item.disabled}
                        title={item.disabled ? item.disabledReason : undefined}
                        onClick={() => {
                            // Hand focus back to the trigger first so a dialog opened by the
                            // action records it as the opener and can return focus on close.
                            triggerRef.current?.focus();
                            setOpenState(false);
                            item.onSelect();
                        }}
                    >
                        {/* The menu sets the icon's size (14) and colour, whatever the caller gave it */}
                        {item.icon && (
                            <span className={`grid place-items-center flex-none [&>svg]:w-3.5 [&>svg]:h-3.5 ${item.disabled ? '' : item.danger ? 'text-danger-deep' : 'text-ink-soft'}`} aria-hidden="true">{item.icon}</span>
                        )}
                        <span className="flex-1 truncate">{item.label}</span>
                    </button>
                ),
            )}
        </div>
    ) : null;

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className={triggerClassName}
                aria-label={label}
                title={label}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                tabIndex={triggerTabIndex}
                onClick={(e) => { stop(e); setOpenState(!open); }}
                onMouseDown={stop}
                onKeyDown={(e) => {
                    stop(e);
                    if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpenState(true); }
                }}
                draggable={false}
            >
                {children ?? <MoreHorizontal className="w-4 h-4" aria-hidden="true" />}
            </button>
            {panel && typeof document !== 'undefined' ? createPortal(panel, document.body) : null}
        </>
    );
};
